import { useState } from 'react'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Alert, Input, Modal, Select, message } from 'antd'
import { BaseOptionType, DefaultOptionType } from 'antd/lib/select'
import Sentry from '~/utils/sentry'
import {
  Button,
  Box,
  Heading,
  Flex,
  Text,
  Card,
} from '@weareberyl/design-system'

import { useScheduleRefresh } from '~/hooks/useScheduleRefresh'
import { generateResultMessage, journeyDescriptionChoices } from './Refund'
import { useValidators } from './hooks'
import {
  InvoiceDocument,
  InvoiceQuery_invoice_JourneyInvoice,
  useRecalculateJourneyInvoiceMutation,
} from '~/gql/generated/graphql'
import { MinuteInput } from '~/components/shared/numberInputs'

type RecalculateJourneyInvoiceProps = {
  invoice: InvoiceQuery_invoice_JourneyInvoice
  onComplete: () => void
}

const RecalculateJourneyInvoice = ({
  invoice,
  onComplete,
}: RecalculateJourneyInvoiceProps) => {
  const scheduleRefresh = useScheduleRefresh([InvoiceDocument])
  const [visible, setVisible] = useState(false)
  const [showingConfirmation, setShowingConfirmation] = useState(false)

  // Form state
  const [description, setDescription] = useState<string | 'Please select'>(
    'Please select',
  )
  const [customDescription, setCustomDescription] = useState<string>('')
  const [amendedDuration, setAmendedDuration] = useState(
    invoice.journey.duration,
  )

  const descriptionValue =
    description.toLowerCase() === 'other' ? customDescription : description

  const { isValid, errorMessage } = useValidators(
    [
      () =>
        descriptionValue === 'Please select' && 'Please select a description',
      () => descriptionValue.length === 0 && 'Please enter a description',
    ],
    [descriptionValue, amendedDuration],
  )

  const [submitReversal, { loading }] = useRecalculateJourneyInvoiceMutation({
    refetchQueries: [InvoiceDocument],
    onCompleted: result => {
      message.info('Request successful', 10)
      if (result.recalculate_journey_invoice?.currency?.status) {
        generateResultMessage(
          result.recalculate_journey_invoice.currency.status,
          'Refund currency',
          result.recalculate_journey_invoice.currency.message ?? null,
        )
        if (
          result.recalculate_journey_invoice.currency.status === 'succeeded'
        ) {
          // Currency refund settling transaction takes a bit of
          // time to come through as it's added by the webhook from Stripe.
          // So schedule an update in a couple of seconds
          scheduleRefresh()
        }
      }

      hideModal()
    },
    onError: err => {
      Sentry.captureException(err)
      onComplete()
      message.error('Something went wrong', 10)
    },
  })

  const buttonAction = () => {
    if (showingConfirmation) {
      submitReversal({
        variables: {
          id: invoice.id,
          description: descriptionValue,
          amendedDuration: amendedDuration,
        },
      })
    } else {
      setShowingConfirmation(true)
    }
  }

  const hideModal = () => {
    setVisible(false)
    setShowingConfirmation(false)
    onComplete()
  }

  return (
    <>
      <Button
        mt={2}
        mr={3}
        title="Recalculate journey charges"
        variant="primary"
        onPress={() => setVisible(true)}
      />

      <Modal
        open={visible}
        zIndex={1050}
        footer={null}
        onCancel={hideModal}
        bodyStyle={{ padding: 0 }}
        width={800}
      >
        <Form onSubmit={buttonAction} title="Refund form">
          <Box pt={4} px={5} zIndex={2}>
            <Heading variant="callout" pt={2} pb={4}>
              Recalculate journey charges
            </Heading>
            <Text>
              <p>Enter the new duration to recalculate the charges.</p>
              <p>Any excess amount charged will be refunded to the customer.</p>
              <p>
                If the charges come to more than the amount already debited,
                then the invoice will go to ouststanding. It can be settled by
                the customer or with the red <em>&quot;settle&quot;</em> button.
              </p>
            </Text>
          </Box>

          <Box py={2} px={5} zIndex={2}>
            <Box bg="tofu" p={3} pl={5} my={2} borderRadius={10}>
              <Box mb={2}>
                <Heading>Amend journey duration</Heading>
              </Box>
              <Flex alignItems="center" style={{ gap: '10px' }}>
                <Box>
                  <Text>Corrected duration</Text>
                </Box>
                <Box style={{ gap: '10px' }}>
                  <Flex alignItems="center" style={{ gap: '10px' }}>
                    <MinuteInput
                      size="large"
                      max={invoice.journey.duration ?? undefined}
                      value={amendedDuration ?? 0}
                      onChange={value => {
                        if (value !== null) {
                          setAmendedDuration(value)
                        }
                      }}
                      title="Amended duration"
                    />
                  </Flex>
                </Box>
              </Flex>
            </Box>
            <Box p={3} pl={5} my={2} style={{ gap: '10px' }}>
              <Box mb={1}>
                <label htmlFor="refund-description" style={{ width: '150px' }}>
                  <Heading>Description</Heading>
                </label>
              </Box>
              <Select
                id="refund-description"
                style={{ flex: 1 }}
                value={description}
                size="large"
                onChange={(selected: string) => {
                  setDescription(selected)
                }}
                showSearch
                placeholder="Please select"
                options={journeyDescriptionChoices}
                filterOption={(
                  search: string,
                  option: BaseOptionType | DefaultOptionType | undefined,
                ) => {
                  return option?.label
                    .toLowerCase()
                    .includes(search.toLowerCase())
                }}
              />
              {description?.toLowerCase() === 'other' && (
                <Input
                  size="large"
                  value={customDescription ?? ''}
                  onChange={event => setCustomDescription(event.target.value)}
                  placeholder="Please enter a custom description"
                  title="Custom description"
                />
              )}
            </Box>
            {errorMessage && (
              <Alert
                message="Opps"
                description={errorMessage}
                type="error"
                showIcon
              />
            )}
          </Box>
        </Form>
        <Card
          p={5}
          borderTopLeftRadius={0}
          borderTopRightRadius={0}
          variant="gray"
          testID="refund-invoice-confirmation"
        >
          {showingConfirmation ? (
            <Flex mr={2}>
              <Flex
                width="50%"
                justifyContent="flex-start"
                alignContent="center"
              >
                <Text>Are you sure?</Text>
              </Flex>
              <Box width="50%">
                <Button
                  title="Confirm"
                  onPress={buttonAction}
                  disabled={!isValid || loading}
                  loading={loading}
                  type="submit"
                />
              </Box>
            </Flex>
          ) : (
            <Button
              testID="refund-invoice-confirmation-btn"
              title="Refund"
              onPress={buttonAction}
              disabled={!isValid}
              loading={loading}
              type="submit"
            />
          )}
        </Card>
      </Modal>
    </>
  )
}

export default RecalculateJourneyInvoice
